import {HttpErrorResponse} from '@angular/common/http';
import {Component, inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {ServiceProviderHub} from '@generated-backend-types';
import {FormGroupComponent} from '../../components/form-group/form-group.component';
import {FormErrorService} from '../../services/form-error-service/form-error.service';
import {AuthService} from '../../services/auth-service/auth.service';
import ServiceProviderHubRegistrationData = ServiceProviderHub.App.Data.ServiceProviderHub.Auth.ServiceProviderHubRegistrationData;

@Component({
  selector: 'app-register',
  imports: [
    ReactiveFormsModule,
    FormGroupComponent,
  ],
  templateUrl: './register.component.html',
})
export class RegisterComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private formErrorService = inject(FormErrorService);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  form = new FormGroup({
    invite_code:           new FormControl({value: '', disabled: true}),
    email:                 new FormControl({value: '', disabled: true}),
    password:              new FormControl('', [Validators.required, Validators.minLength(12)]),
    password_confirmation: new FormControl('', [Validators.required, Validators.minLength(12)]),
  });

  ngOnInit(): void {
    const inviteCode = this.readQueryParam('invite_code');
    const email = this.readQueryParam('email');

    if (!inviteCode || !email) {
      this.toastr.warning('Der Registrierungslink ist ungültig oder unvollständig.');
      void this.router.navigate(['/login']);
      return;
    }

    this.form.controls.invite_code.setValue(inviteCode);
    this.form.controls.email.setValue(email);
  }

  onSubmit() {
    const data: ServiceProviderHubRegistrationData = {
      invite_code: this.form.getRawValue().invite_code ?? '',
      email: this.form.getRawValue().email ?? '',
      password: this.form.value.password ?? '',
      password_confirmation: this.form.value.password_confirmation ?? '',
    };

    this.authService.register(data).subscribe({
      next: () => {
        this.router.navigate(['login']).then(
          () => this.toastr.success('Registrierung erfolgreich'),
        );
      },
      error: (response: HttpErrorResponse) => {
        switch (response.status) {
          case 422:
            this.formErrorService.mapErrorToForm(this.form, response);
            return;
          case 419:
            this.toastr.error('Ihre Sitzung ist abgelaufen, die Seite wird neu geladen.');
            this.reloadPage();
            return;
          case 404:
          case 410:
            this.toastr.error('Der Registrierungslink ist ungültig oder abgelaufen.');
            void this.router.navigate(['/login']);
            return;
          case 0:
            this.toastr.error('Keine Verbindung zum Server. Bitte versuchen Sie es erneut.');
            return;
          case 400:
          case 423:
            // Interceptor already toasts the backend message.
            return;
          default:
            this.toastr.error('Registrierung nicht möglich.');
        }
      },
    });
  }

  private getRawSearch(): string {
    return window.location.search;
  }

  private reloadPage(): void {
    window.location.reload();
  }

  private readQueryParam(name: string): string | null {
    const fromAngular = this.route.snapshot.queryParamMap.get(name);
    const match = this.getRawSearch().match(new RegExp(`[?&]${name}=([^&]*)`));
    if (!match) return fromAngular;
    try {
      const fromUrl = decodeURIComponent(match[1]);
      if (fromUrl.includes('+') && fromAngular?.includes(' ')) {
        return fromUrl;
      }
      return fromAngular ?? fromUrl;
    } catch {
      return fromAngular;
    }
  }
}
